// Which saved things fill a day (moved out of itinerary.ts so deno tests load it: it imports only the shared rules).
import { dropInWindow, fitsDay, fixedDay, kmBetween, lateToday, legMs, type DropInItem, type FitItem } from './fit.ts'

/** A day never holds more than this many things when it is filled for them. */
export const DAY_CAP = 4
type FillItem = FitItem & DropInItem & { lat: number | null; lng: number | null }
type FillEntry = { itemId: string; item: FillItem; tz: string | null; gone?: string }

/**
 * Which saved things fill a day: every saved thing that fits it, up to DAY_CAP (blind test r2, Tasha: Fill must use what
 * the picker counted). Events on that day by time (one that clashes with an earlier one goes to Also fits), then places
 * closest to the day's centre. The pace only decides how many suggestions round it out.
 * Blind round 3 (marco): a drop-in window (a market 10 AM-3 PM, dropInWindow) is not a block of time: you go for part of
 * it, so it never clashes with a set-time show inside it (Stones Throw at 2 PM was dropped from Sat by SOAP OPERA).
 */
export function fillOrder<E extends FillEntry>(entries: E[], ymd: string, anchor?: E | null, now: Date = new Date()): { fill: E[]; also: E[] } {
  const all = entries.filter((e) => fitsDay(e, ymd, now) && e.itemId !== anchor?.itemId)
  // B-089: on today, what can no longer happen (closed for the day, already started) is never filled in; it stays on Also fits
  const late = all.filter((e) => lateToday(e.item, ymd, e.tz, now))
  const fit = all.filter((e) => !late.includes(e))
  const evs = fit.filter((e) => fixedDay(e.item, e.tz)).sort((a, b) => String(a.item.start_at).localeCompare(String(b.item.start_at)))
  const keptEv: E[] = anchor && fixedDay(anchor.item, anchor.tz) ? [anchor] : []
  const clash: E[] = []
  const span = (e: E) => { const s = new Date(e.item.start_at!).getTime(); return [s, e.item.end_at ? new Date(e.item.end_at).getTime() : s + 2 * 3600e3] }
  const blocks = (e: E) => !dropInWindow(e.item, e.tz)
  for (const e of evs) {
    const [s, en] = span(e)
    // B-131: two events also clash when there is no time to get from one to the other (yoga ends 7:00, a social starts 7:00)
    if (blocks(e) && keptEv.some((k) => { if (!blocks(k)) return false; const [ks, ke] = span(k); const leg = legMs(k.item, e.item); return s < ke + leg && en + leg > ks })) clash.push(e)
    else keptEv.push(e)
  }
  const places = fit.filter((e) => !fixedDay(e.item, e.tz))
  const centre = anchor?.item ?? keptEv[0]?.item ?? null
  if (centre) places.sort((a, b) => (kmBetween(centre, a.item) ?? 99) - (kmBetween(centre, b.item) ?? 99))
  const base = keptEv.filter((e) => e.itemId !== anchor?.itemId)
  const room = Math.max(0, DAY_CAP - (anchor ? 1 : 0) - base.length)
  const fill = [...base, ...places.slice(0, room)].slice(0, DAY_CAP - (anchor ? 1 : 0))
  const used = new Set(fill.map((e) => e.itemId))
  return { fill, also: [...fit.filter((e) => !used.has(e.itemId) && !clash.includes(e)), ...late] }
}
